import {
  AssessmentFormat,
  CoachPortalInAppAssessment,
  CoachPortalSession,
  CoachSessionStudent,
  InAppAssessmentResultSubmission,
  isProgressMonitorFormat,
  SessionStatus,
  shouldShowGradeLevelAssessmentV2CanRetell,
  StudentYear,
} from '@airreading/common/data';

/**
 * TODO: per-format durations land with the stats ticket. The server already measures
 * `progress_monitor_sounds` over 30 seconds rather than 60.
 */
export const DEFAULT_ASSESSMENT_DURATION_SECONDS = 60;

/**
 * One student row in the assessment dropdown, paired with the runnable contents for the selected
 * timed-content kind. Progress monitor can have several (passage and/or word list); GLA and fluency
 * check typically have one.
 */
export interface InAppAssessmentStudent {
  student: CoachSessionStudent;
  displayName: string;
  gradeLabel: string | null;
  assessments: CoachPortalInAppAssessment[];
}

export function canRunInAppAssessments(session: CoachPortalSession): boolean {
  return session.status !== SessionStatus.cancelled && (session.inAppAssessments ?? []).length > 0;
}

/**
 * Words stay in the same order as a whitespace split. `breaksBefore[i]` is 1 when `words[i]`
 * starts a new line, including after a blank line between paragraphs.
 */
export function layoutPassage(passage: string): { words: string[]; breaksBefore: number[] } {
  const normalized = passage.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const words: string[] = [];
  const breaksBefore: number[] = [];
  let seenWord = false;

  for (const line of normalized.split('\n')) {
    const tokens = line.split(/[^\S\n]+/).filter((token) => token.length > 0);
    if (tokens.length === 0) {
      continue;
    }
    tokens.forEach((token, index) => {
      breaksBefore.push(seenWord && index === 0 ? 1 : 0);
      words.push(token);
      seenWord = true;
    });
  }

  return { words, breaksBefore };
}

/** Words are delimited by any run of whitespace, so line breaks never fuse two words together. */
export function tokenizePassage(passage: string): string[] {
  return layoutPassage(passage).words;
}

export function passageLineBreaksBefore(passage: string): number[] {
  return layoutPassage(passage).breaksBefore;
}

/** Stats already stored on a completed result, used to redraw the result page after the window reopens. */
export function inAppAssessmentServerStats(
  assessment:
    | Pick<
        CoachPortalInAppAssessment,
        'assessmentFinished' | 'totalWordsRead' | 'incorrectWordsCount' | 'totalSecondsSpent' | 'finishedEarly'
      >
    | undefined,
): {
  totalWordsRead: number | null;
  incorrectWordsCount: number | null;
  totalSecondsSpent: number | null;
  finishedEarly: boolean | null;
} | null {
  if (assessment?.assessmentFinished !== true) {
    return null;
  }
  return {
    totalWordsRead: assessment.totalWordsRead,
    incorrectWordsCount: assessment.incorrectWordsCount,
    totalSecondsSpent: assessment.totalSecondsSpent,
    finishedEarly: assessment.finishedEarly,
  };
}

export function formatCountdown(secondsRemaining: number): string {
  const clamped = Math.max(0, Math.trunc(secondsRemaining));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export type AssessmentPhase =
  'idle' | 'running' | 'paused' | 'confirmingEarlyFinish' | 'earlyFinishReady' | 'timeUp' | 'submitting' | 'done';

/** True after Start until the attempt is reset or submitted. */
export function isInAppAssessmentAttemptActive(phase: AssessmentPhase): boolean {
  return phase !== 'idle' && phase !== 'done';
}

/** True when the completion banner is showing, which is the only time the redo confirmation is visible. */
export function showsSavedInAppAssessmentResult(
  phase: AssessmentPhase,
  assessment: Parameters<typeof inAppAssessmentServerStats>[0],
  submittedStats: InAppAssessmentStats | null,
): boolean {
  const serverStats = inAppAssessmentServerStats(assessment);
  const displayStats = phase === 'done' && submittedStats != null ? submittedStats : serverStats;
  return (phase === 'idle' || phase === 'done') && (displayStats != null || assessment?.assessmentFinished === true);
}

/**
 * Word-list switch, finish-early, and redo confirmations block the assessment window.
 * The retell question is not one of these dialogs and must not lock the window.
 */
export function inAppAssessmentConfirmationLocked({
  wordListSwitchOpen,
  earlyFinishConfirmOpen,
  redoPromptOpen,
  showingSavedResult,
}: {
  wordListSwitchOpen: boolean;
  earlyFinishConfirmOpen: boolean;
  redoPromptOpen: boolean;
  showingSavedResult: boolean;
}): boolean {
  return wordListSwitchOpen || earlyFinishConfirmOpen || (redoPromptOpen && showingSavedResult);
}

/** Errors can only be marked on words the student is treated as having read. */
export function canMarkInAppAssessmentError(wordIndex: number, lastWordIndex: number | null): boolean {
  return lastWordIndex === null || wordIndex <= lastWordIndex;
}

/** Last word must cover every already-marked error. */
export function canSelectInAppAssessmentLastWord(wordIndex: number, markedWordIndexes: readonly number[]): boolean {
  return markedWordIndexes.every((index) => index <= wordIndex);
}

export interface InAppAssessmentStats {
  totalWordsRead: number;
  incorrectWordsCount: number;
  totalSecondsSpent: number;
  finishedEarly: boolean;
}

export function buildInAppAssessmentResultSubmission({
  words,
  markedWordIndexes,
  lastWordIndex,
  secondsRemaining,
  durationSeconds = DEFAULT_ASSESSMENT_DURATION_SECONDS,
  finishedEarly,
  noTimerContent = false,
  canRetell,
}: {
  words: string[];
  markedWordIndexes: readonly number[];
  lastWordIndex: number | null;
  secondsRemaining: number;
  durationSeconds?: number;
  finishedEarly: boolean;
  /** Content without a timer counts every item and is never reported as an early finish. */
  noTimerContent?: boolean;
  canRetell?: boolean;
}): InAppAssessmentResultSubmission {
  const reportedFinishedEarly = finishedEarly && !noTimerContent;
  const totalWordsRead = reportedFinishedEarly || noTimerContent ? words.length : (lastWordIndex ?? 0) + 1;
  const incorrectWords = markedWordIndexes
    .filter((index) => index < totalWordsRead)
    .map((index) => words[index])
    .filter((word): word is string => word != null);
  return {
    incorrectWords,
    incorrectWordsCount: incorrectWords.length,
    totalWordsRead,
    // Time-up and no-timer content use the full measured window; early finish uses elapsed seconds.
    totalSecondsSpent: reportedFinishedEarly ? Math.max(1, durationSeconds - secondsRemaining) : durationSeconds,
    finishedEarly: reportedFinishedEarly,
    assessmentFinished: true,
    ...(canRetell === undefined ? {} : { canRetell }),
  };
}

export type InAppProgressMonitorPart = 'passage' | 'word-list';

/** Passage wins when `readingPassage` is non-blank, even if `words` is also set. */
export function inAppProgressMonitorPart(
  assessment: Pick<CoachPortalInAppAssessment, 'readingPassage' | 'words'>,
): InAppProgressMonitorPart | null {
  if (assessment.readingPassage?.trim()) {
    return 'passage';
  }
  if (assessment.words.length > 0) {
    return 'word-list';
  }
  return null;
}

/**
 * Tab copy for a progress-monitor content. Letter-name and letter-sound passages keep the passage
 * slot (before the word list) and only change the label.
 */
export function inAppProgressMonitorTabLabel(
  assessment: Pick<CoachPortalInAppAssessment, 'format' | 'readingPassage' | 'words'>,
): string {
  if (inAppProgressMonitorPart(assessment) !== 'passage') {
    return 'Word list';
  }
  if (assessment.format === AssessmentFormat.progress_monitor_letter_names_v2) {
    return 'Letter names';
  }
  if (assessment.format === AssessmentFormat.progress_monitor_sounds_v2) {
    return 'Letter sounds';
  }
  return 'Passage';
}

/**
 * Letter-name word lists have no timer: the coach marks errors and submits, with no countdown
 * and no early finish.
 */
export function isNoTimerLetterNamesWordList(
  assessment: Pick<CoachPortalInAppAssessment, 'format' | 'readingPassage' | 'words'> | undefined,
): boolean {
  if (!assessment) {
    return false;
  }
  return (
    assessment.format === AssessmentFormat.progress_monitor_letter_names_v2 &&
    inAppProgressMonitorPart(assessment) === 'word-list'
  );
}

/** Word lists, plus letter-name and letter-sound passages, use the per-word box grid. */
export function shouldBoxInAppAssessmentWords(
  assessment: Pick<CoachPortalInAppAssessment, 'format' | 'readingPassage' | 'words'> | undefined,
): boolean {
  if (!assessment) {
    return false;
  }
  const part = inAppProgressMonitorPart(assessment);
  if (part === 'word-list') {
    return true;
  }
  return (
    part === 'passage' &&
    (assessment.format === AssessmentFormat.progress_monitor_letter_names_v2 ||
      assessment.format === AssessmentFormat.progress_monitor_sounds_v2)
  );
}

type InAppResultState = {
  phase: AssessmentPhase;
  submittedStats: InAppAssessmentStats | null;
};

export function isInAppAssessmentResultSubmitted(
  assessment: Pick<CoachPortalInAppAssessment, 'assessmentResultId' | 'assessmentFinished'>,
  state: { submittedStats: InAppAssessmentStats | null } | undefined,
): boolean {
  return state?.submittedStats != null || assessment.assessmentFinished === true;
}

function isRunnableInAppAssessment(assessment: CoachPortalInAppAssessment): boolean {
  if (isProgressMonitorFormat(assessment.format)) {
    return inAppProgressMonitorPart(assessment) != null;
  }
  return true;
}

/** True when any runnable result in the session, across every kind, has not been submitted. */
export function sessionHasIncompleteInAppAssessment(
  assessments: readonly CoachPortalInAppAssessment[],
  stateByResultId: Record<string, { submittedStats: InAppAssessmentStats | null }>,
): boolean {
  return assessments.some(
    (assessment) =>
      isRunnableInAppAssessment(assessment) &&
      !isInAppAssessmentResultSubmitted(assessment, stateByResultId[assessment.assessmentResultId]),
  );
}

export type InAppStudentStatus = 'pending' | 'in_progress' | 'passage_done' | 'done';

export function inAppStudentStatus(
  assessments: readonly CoachPortalInAppAssessment[],
  stateByResultId: Record<string, InAppResultState>,
): InAppStudentStatus {
  const runnable = assessments.filter(isRunnableInAppAssessment);
  if (runnable.length === 0) {
    return 'pending';
  }
  if (
    runnable.every((assessment) =>
      isInAppAssessmentResultSubmitted(assessment, stateByResultId[assessment.assessmentResultId]),
    )
  ) {
    return 'done';
  }
  const started = runnable.some((assessment) => {
    const state = stateByResultId[assessment.assessmentResultId];
    return state != null && state.phase !== 'idle' && !isInAppAssessmentResultSubmitted(assessment, state);
  });
  if (started) {
    return 'in_progress';
  }
  const passage = runnable.find((assessment) => inAppProgressMonitorPart(assessment) === 'passage');
  const wordList = runnable.find((assessment) => inAppProgressMonitorPart(assessment) === 'word-list');
  if (
    passage &&
    wordList &&
    isInAppAssessmentResultSubmitted(passage, stateByResultId[passage.assessmentResultId]) &&
    !isInAppAssessmentResultSubmitted(wordList, stateByResultId[wordList.assessmentResultId])
  ) {
    return 'passage_done';
  }
  return 'pending';
}

/** Name to show when leaving this student would skip a required word list. Null when the switch is allowed. */
export function wordListSwitchBlockName(
  student: InAppAssessmentStudent,
  stateByResultId: Record<string, { submittedStats: InAppAssessmentStats | null }>,
): string | null {
  const passage = student.assessments.find((assessment) => inAppProgressMonitorPart(assessment) === 'passage');
  const wordList = student.assessments.find((assessment) => inAppProgressMonitorPart(assessment) === 'word-list');
  if (!passage || !wordList) {
    return null;
  }
  if (
    !isInAppAssessmentResultSubmitted(passage, stateByResultId[passage.assessmentResultId]) ||
    isInAppAssessmentResultSubmitted(wordList, stateByResultId[wordList.assessmentResultId])
  ) {
    return null;
  }
  return student.displayName.trim() || 'This student';
}

export function shouldShowInAppRetell(
  assessment: CoachPortalInAppAssessment | undefined,
  grade: StudentYear | null | undefined,
): boolean {
  if (!assessment) {
    return false;
  }
  if (assessment.format === AssessmentFormat.grade_level_fluency_v2) {
    return shouldShowGradeLevelAssessmentV2CanRetell(grade);
  }
  return (
    assessment.format === AssessmentFormat.progress_monitor_words_v2 &&
    inAppProgressMonitorPart(assessment) === 'passage'
  );
}
